import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { environment } from '@environments/environment';
import { AppModule } from './app/app.module';
import { isGmailInAppBrowserIOS } from './app/shared/utils/browser.utils';

import { defineAteIcon, setAssetPath } from '@addtoevent/icons/dist/vanilla';
import { defineCustomElements as components } from '@addtoevent/web-components/dist/loader';

import * as Sentry from '@sentry/angular';

Sentry.init({
  dsn: environment.sentryDsn,
  environment: environment.env === 'prod' ? 'production' : 'uat',
  integrations: integrations => [...integrations.filter(integration => integration.name !== 'Breadcrumbs'), Sentry.browserTracingIntegration()],
  tracePropagationTargets: [environment.sentryTracingOrigin],
  tracesSampleRate: environment.sentryTracesSampleRate,
  release: environment.version,

  // Filter out known noise: browser extensions, bots, non-actionable browser errors
  denyUrls: [
    // Chrome/Firefox extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i
  ],

  ignoreErrors: [
    // Angular differ errors (e.g. NG0901 - ngFor receiving non-iterable)
    'Cannot find a differ supporting object',
    'NG0901',
    // Network / fetch errors that are not actionable
    'Failed to fetch',
    'NetworkError',
    'Load failed',
    // Lazy-loaded chunk failures (user navigating during deploy)
    /Loading chunk \d+ failed/,
    /ChunkLoadError/,
    // ResizeObserver noise from browser internals / third-party libs
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    // Firebase transient network errors — Firebase already retries internally; these are unactionable
    /auth\/network-request-failed/,
    // Non-Error rejections from third-party scripts
    'Non-Error exception captured',
    'Non-Error promise rejection captured',
    // Safari/iOS quirks
    "Can't find variable: $",
    'undefined is not an object'
  ],

  beforeSend(event, hint) {
    // Drop events with no stack trace (usually from browser extensions or bots)
    const exception = hint?.originalException;
    if (!exception || typeof exception === 'string') {
      return event;
    }
    // Drop events originating from browser extension scripts
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    const fromExtension = frames.some(f => f.filename?.startsWith('chrome-extension://') || f.filename?.startsWith('moz-extension://'));
    if (fromExtension) {
      return null;
    }
    return event;
  }
});

if (environment.production) {
  enableProdMode();
}

/**
 * Detects whether the app is running inside the Gmail in-app browser on iOS and,
 * if so, renders a full-screen overlay instructing the user to open in Safari or
 * Chrome. Returns true when the overlay is shown so the caller can skip Angular
 * bootstrap (there is nothing useful to render without working Auth).
 *
 * Why pre-bootstrap: the GSA/Gmail WKWebView blocks localStorage and IndexedDB,
 * which prevents Firebase Auth from initialising. Angular never renders, leaving a
 * blank white screen. We must intercept before platformBrowserDynamic() is called.
 *
 * Detection notes:
 *  - "GSA" appears in the UA for Gmail and other Google apps on iOS using WKWebView.
 *  - "Gmail" is present in some variants of the Gmail iOS UA.
 *  - iOS guard prevents false positives on Android — Gmail on Android uses Chrome
 *    Custom Tabs (full Chrome) which supports localStorage, so no issue there.
 *  - CriOS (Chrome on iOS) and Safari are not matched and are unaffected.
 */
function bootstrap(): void {
  platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch(err => {
      console.error(err);
      // Show a visible fallback so users don't see a silent blank screen.
      // Inline styles only — Angular and CSS frameworks are unavailable at this point.
      const el = document.createElement('div');
      el.style.cssText =
        'position:fixed;top:0;left:0;width:100%;height:100%;background:#ffffff;' +
        'display:flex;align-items:center;justify-content:center;padding:24px;' +
        'box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;' +
        'text-align:center';
      el.innerHTML =
        '<p style="font-size:16px;color:#333333;line-height:1.6;max-width:340px;margin:0">' +
        'Something went wrong. Please refresh the page or try opening in Safari.' +
        '</p>';
      document.body.appendChild(el);
    });
}

/**
 * Detects whether the app is running inside the Gmail in-app browser on iOS and,
 * if so, renders a full-screen overlay instructing the user to open in Safari or
 * Chrome. Returns true when the overlay is shown so the caller can skip Angular
 * bootstrap (there is nothing useful to render without working Auth).
 *
 * The overlay includes a "Try anyway" link that dismisses it and bootstraps
 * normally — this lets us gather real-world evidence that the Firebase
 * inMemoryPersistence fallback (in app.module.ts) works for Gmail iOS users
 * before we commit to removing the overlay entirely.
 *
 * Why pre-bootstrap: the GSA/Gmail WKWebView blocks localStorage and IndexedDB,
 * which prevents Firebase Auth from initialising. Angular never renders, leaving a
 * blank white screen. We must intercept before platformBrowserDynamic() is called.
 *
 * Detection notes:
 *  - "GSA" appears in the UA for Gmail and other Google apps on iOS using WKWebView.
 *  - "Gmail" is present in some variants of the Gmail iOS UA.
 *  - iOS guard prevents false positives on Android — Gmail on Android uses Chrome
 *    Custom Tabs (full Chrome) which supports localStorage, so no issue there.
 *  - CriOS (Chrome on iOS) and Safari are not matched and are unaffected.
 */
function showInAppBrowserOverlay(): boolean {
  if (!isGmailInAppBrowserIOS()) {
    return false;
  }

  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'alert');
  overlay.style.cssText =
    'position:fixed;top:0;left:0;width:100%;height:100%;background:#ffffff;' +
    'z-index:999999;display:flex;flex-direction:column;align-items:center;' +
    'justify-content:center;padding:32px 24px;box-sizing:border-box;' +
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;text-align:center';

  const tryAnywayId = 'ate-try-anyway';
  overlay.innerHTML =
    '<h2 style="font-size:20px;font-weight:700;color:#111111;margin:0 0 16px;line-height:1.3">' +
    'The Google browser is currently incompatible with this feature on Add To Event.' +
    '</h2>' +
    '<p style="font-size:16px;color:#555555;line-height:1.6;margin:0 0 24px;max-width:340px">' +
    'To use this app, please tap the \u2018Share\u2019 icon and select ' +
    '\u2018Open in Safari or Google Chrome\u2019.' +
    '</p>' +
    '<button id="' + tryAnywayId + '" ' +
    'style="background:none;border:none;padding:0;font-size:14px;color:#888888;' +
    'text-decoration:underline;cursor:pointer;font-family:inherit">' +
    'Try opening in this browser' +
    '</button>';

  document.body.appendChild(overlay);

  document.getElementById(tryAnywayId)?.addEventListener('click', () => {
    overlay.remove();
    bootstrap();
  });

  return true;
}

if (!showInAppBrowserOverlay()) {
  bootstrap();
}

components(window);

// Register the framework-free <ate-icon> element instead of the old lazy
// Stencil icons loader — no separate icons .esm.js is downloaded. Icon SVGs
// load from the app's own co-deployed /svg/ assets (see angular.json).
setAssetPath(document.baseURI);
defineAteIcon();
